import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, Calendar, Info } from 'lucide-react';
import { toast } from 'sonner';

const MyPayroll = () => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const res = await axios.get('/payroll/my-documents');
      setDocuments(res.data);
    } catch (error) {
      console.error('Error loading payroll:', error);
      toast.error('Fehler beim Laden der Abrechnungen');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (doc) => {
    try {
      // Mark as viewed
      await axios.post(`/payroll/${doc.document_id}/mark-viewed`);
      
      // In production: Open PDF in new tab
      // window.open(doc.file_url, '_blank');
      
      toast.success('Abrechnung wird geöffnet...');
      
      // Update local state
      setDocuments(prev => prev.map(d => 
        d.document_id === doc.document_id 
          ? { ...d, viewed: true, viewed_at: new Date().toISOString() }
          : d
      ));
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Fehler beim Öffnen');
    }
  };

  const groupByYear = (docs) => {
    const grouped = {};
    docs.forEach(doc => {
      const year = doc.year || new Date(doc.month).getFullYear();
      if (!grouped[year]) grouped[year] = [];
      grouped[year].push(doc);
    });
    return grouped;
  };

  const groupedDocuments = groupByYear(documents);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-4xl font-bold text-white">Meine Lohnabrechnungen</h1>
        <p className="text-zinc-400 mt-2">Ihre monatlichen Lohnabrechnungen zum Download</p>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-900/20 border-blue-700">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-400 font-medium">Wichtige Informationen</p>
            <p className="text-sm text-zinc-300 mt-1">
              Ihre Lohnabrechnungen werden monatlich hochgeladen. Sie erhalten eine Benachrichtigung, 
              sobald eine neue Abrechnung verfügbar ist. Bewahren Sie Ihre Abrechnungen für mindestens 
              3 Jahre auf.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Documents List by Year */}
      <div className="space-y-6">
        {Object.keys(groupedDocuments).length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Noch keine Abrechnungen</h3>
              <p className="text-zinc-400">
                Ihre Lohnabrechnungen werden hier angezeigt, sobald sie von der Verwaltung hochgeladen wurden.
              </p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedDocuments).sort((a, b) => b[0] - a[0]).map(([year, docs]) => (
            <Card key={year} className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Jahr {year}
                  </CardTitle>
                  <Badge className="bg-zinc-800 text-zinc-400">
                    {docs.length} Abrechnung(en)
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {docs.sort((a, b) => b.month.localeCompare(a.month)).map(doc => (
                    <div
                      key={doc.document_id}
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="h-12 w-12 rounded-lg bg-blue-900/30 flex items-center justify-center">
                          <FileText className="h-6 w-6 text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-white">
                            {new Date(doc.month + '-01').toLocaleDateString('de-DE', { 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </p>
                          <p className="text-sm text-zinc-400">{doc.file_name}</p>
                          {doc.notes && (
                            <p className="text-xs text-zinc-500 mt-1">{doc.notes}</p>
                          )}
                        </div>
                        
                        <div className="flex flex-col items-end gap-2">
                          {doc.viewed ? (
                            <Badge className="bg-green-900/30 text-green-400 border-green-700">
                              Gesehen
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-900/30 text-yellow-400 border-yellow-700 animate-pulse">
                              Neu
                            </Badge>
                          )}
                          
                          <p className="text-xs text-zinc-500">
                            Hochgeladen: {new Date(doc.uploaded_at).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => handleView(doc)}
                        size="sm"
                        className="ml-4"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Ansehen
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Summary Stats */}
      {documents.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-2xl font-bold text-white">{documents.length}</p>
                <p className="text-sm text-zinc-400">Gesamt Abrechnungen</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {documents.filter(d => d.viewed).length}
                </p>
                <p className="text-sm text-zinc-400">Gesehen</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-400">
                  {documents.filter(d => !d.viewed).length}
                </p>
                <p className="text-sm text-zinc-400">Ungelesen</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MyPayroll;
